/**
 * 14f — Marking · accept the batch, look at the rest.
 *
 * Follows `design/screens/14f.html`: the batch is one decision at the top, stated in
 * plain numbers, and only what SchoolVerse is unsure about is presented individually. The
 * screen is titled after what is being marked, not after the event.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useAction, useQuery } from '../../lib/useQuery';
import { BandChip, Btn, Chip, DesignShell, Empty, ErrorNote, Loading, S, type Band } from '../../design/kit';
import { CriterionPanel } from '../teacher/criterionPanel';

interface Item {
  id: string;
  answer: string[];
  mark?: number;
  band?: Band;
  feedback?: string;
  status: string;
  studentName: string;
  question: {
    id: string; stem: string; marks: number; options: string[]; type: string;
    criterionLetter?: string;
    rubric: { level: Band; descriptor: string }[];
  } | null;
}

interface Payload {
  event: { id: string; title: string; status: string };
  cohortName: string;
  focus: { id: string; title: string; type: string } | null;
  totalAnswers: number;
  batch: { count: number; marks: number; items: Item[] };
  review: Item[];
  marked: Item[];
  progress: { total: number; settled: number };
}

export function Marking() {
  const { id = '' } = useParams();
  const { school } = useSession();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useQuery<Payload>(`/marking/events/${id}`);
  const [showBatch, setShowBatch] = useState(false);
  const [open, setOpen] = useState<Item | null>(null);
  const [mark, setMark] = useState(0);
  const [feedback, setFeedback] = useState('');

  const accept = useAction(async () => {
    await api.post(`/marking/events/${id}/accept-batch`);
    reload();
  });
  const draft = useAction(async () => {
    await api.post(`/marking/events/${id}/draft`);
    reload();
  });
  const save = useAction(async (responseId: string) => {
    const res = await api.post<{ revealed?: boolean }>(`/marking/responses/${responseId}`, { mark, feedback });
    setOpen(null);
    // Coaching mode: your mark unseals the AI's (§5.4) — go straight to the comparison (B.8).
    if (res.revealed) navigate(`/teacher/coaching/${responseId}`);
    else reload();
  });

  const shell = (children: React.ReactNode) => (
    <DesignShell
      brand={school?.name}
      title={
        <span>
          Marking{data?.focus ? ` · ${data.focus.title}` : ''}
          {data ? (
            <span style={{ ...S.mutedXs, fontWeight: 500, marginLeft: '12px' }}>
              {data.totalAnswers} answer{data.totalAnswers === 1 ? '' : 's'} · {data.cohortName}
            </span>
          ) : null}
        </span>
      }
      actions={
        <Btn kind="primary" to={`/teacher/events/${id}/release`} disabled={!data || data.progress.settled === 0}>
          Release results
        </Btn>
      }
    >
      {children}
    </DesignShell>
  );

  if (loading && !data) return shell(<Loading />);
  if (error || !data) return shell(<ErrorNote error={error} retry={reload} />);

  return shell(
    <>
      {/* The batch: one decision, stated the way the design states it. */}
      {data.batch.count > 0 && (
        <div style={{ ...S.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
          <div style={{ maxWidth: '620px' }}>
            <div style={{ fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-.01em' }}>
              {data.batch.count} answer{data.batch.count === 1 ? '' : 's'} match your mark scheme
            </div>
            <p style={{ ...S.mutedSm, lineHeight: 1.65, margin: '6px 0 0' }}>
              SchoolVerse has drafted a mark and a comment for each. Accept them together and you are left with the{' '}
              {data.review.length} it is unsure about.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '9px' }}>
            <Btn kind="secondary" onClick={() => setShowBatch((v) => !v)}>
              {showBatch ? 'Hide them' : `See all ${data.batch.count} first`}
            </Btn>
            <Btn kind="primary" onClick={() => void accept.run()} disabled={accept.pending}>
              {accept.pending ? 'Accepting…' : `Accept all ${data.batch.count}`}
            </Btn>
          </div>
          {accept.error ? <ErrorNote error={accept.error} /> : null}
        </div>
      )}

      {showBatch && (
        <div style={S.cardFlush}>
          {data.batch.items.map((it, i) => (
            <div
              key={it.id}
              style={{ padding: '13px 20px', borderTop: i === 0 ? 'none' : '1px solid var(--c-line-soft)', display: 'flex', alignItems: 'center', gap: '14px' }}
            >
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, width: '160px' }}>{it.studentName}</span>
              <span style={{ ...S.mutedSm, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.answer.join(' ')}
              </span>
              <span style={{ fontSize: '0.7812rem', color: 'var(--c-ink-3)' }}>
                {it.mark ?? 0} / {it.question?.marks ?? 0}
              </span>
              {it.band ? <BandChip band={it.band} /> : null}
            </div>
          ))}
        </div>
      )}

      {/* What still needs a human. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
        <span style={S.sectionLabel}>
          {data.review.length === 0 ? 'Nothing left to look at' : `${data.review.length} SchoolVerse is unsure about`}
        </span>
        {data.review.length > 0 && (
          <Btn kind="secondarySm" onClick={() => void draft.run()} disabled={draft.pending}>
            {draft.pending ? 'Drafting…' : '✦ Draft marks for these'}
          </Btn>
        )}
      </div>

      {data.review.length === 0 ? (
        <div style={S.cardFlush}>
          <Empty
            title="Every answer has a mark"
            body="Release when you are ready — you choose exactly what students see."
            action={<Btn kind="primary" to={`/teacher/events/${id}/release`}>Release results</Btn>}
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {data.review.map((it) => (
            <div key={it.id} style={S.card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{it.studentName}</span>
                <span style={S.mutedXs}>{it.question?.marks ?? 0} marks</span>
              </div>
              <p style={{ ...S.mutedSm, margin: '8px 0 6px' }}>{it.question?.stem}</p>
              <div style={{ background: 'var(--c-surface-2)', borderRadius: '9px', padding: '12px 14px', fontSize: '0.8125rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {it.answer.join('\n') || <span style={S.muted}>No response</span>}
              </div>
              <div style={{ display: 'flex', gap: '9px', marginTop: '12px' }}>
                <Btn
                  kind="primarySm"
                  onClick={() => {
                    setOpen(it);
                    setMark(it.mark ?? 0);
                    setFeedback(it.feedback ?? '');
                  }}
                >
                  Mark this one
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {draft.error ? <ErrorNote error={draft.error} /> : null}

      {open && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,20,28,.42)', display: 'grid', placeItems: 'center', padding: '20px', zIndex: 100 }}
          onMouseDown={(e) => e.target === e.currentTarget && setOpen(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Mark ${open.studentName}`}
            style={{ background: '#fff', borderRadius: '14px', padding: '26px', width: 'min(680px, 100%)', maxHeight: '88vh', overflowY: 'auto' }}
          >
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '4px' }}>{open.studentName}</div>
            <p style={{ ...S.mutedSm, marginTop: 0 }}>{open.question?.stem}</p>

            <div style={{ background: 'var(--c-surface-2)', borderRadius: '9px', padding: '12px 14px', fontSize: '0.8125rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {open.answer.join('\n') || 'No response'}
            </div>

            {/*
              A criterion is judged by matching the work to a descriptor, so the rubric stops
              being reference material beside a number box and becomes the control itself.
            */}
            {open.question?.type === 'criterion' ? (
              <CriterionPanel
                rubric={open.question.rubric}
                letter={open.question.criterionLetter}
                mark={mark}
                onMark={setMark}
              />
            ) : (open.question?.rubric.length ?? 0) > 0 ? (
              <div style={{ marginTop: '16px', display: 'grid', gap: '8px' }}>
                <span style={S.sectionLabel}>Rubric</span>
                {open.question!.rubric.map((r) => (
                  <div key={r.level} style={{ display: 'flex', gap: '11px', alignItems: 'flex-start' }}>
                    <span style={{ width: '112px', flex: 'none' }}><BandChip band={r.level} /></span>
                    <span style={{ ...S.mutedSm, lineHeight: 1.55 }}>{r.descriptor}</span>
                  </div>
                ))}
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginTop: '18px', flexWrap: 'wrap' }}>
              {/* A criterion's level came from the panel above; a second number box would
                  offer a way to disagree with it. */}
              <label style={{ display: open.question?.type === 'criterion' ? 'none' : 'grid', gap: '6px' }}>
                <span style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--c-ink-2)' }}>
                  Mark out of {open.question?.marks ?? 0}
                </span>
                <input
                  type="number"
                  min={0}
                  max={open.question?.marks ?? 0}
                  value={mark}
                  onChange={(e) => setMark(Number(e.target.value))}
                  style={{ width: '110px', border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem' }}
                />
              </label>
              <label style={{ display: 'grid', gap: '6px', flex: 1, minWidth: '220px' }}>
                <span style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--c-ink-2)' }}>Feedback for the student</span>
                <input
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="One sentence they can act on"
                  style={{ border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem' }}
                />
              </label>
            </div>

            {save.error ? <ErrorNote error={save.error} /> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', marginTop: '20px' }}>
              <Btn kind="secondary" onClick={() => setOpen(null)}>Cancel</Btn>
              <Btn kind="primary" onClick={() => void save.run(open.id)} disabled={save.pending}>
                {save.pending ? 'Saving…' : 'Save the mark'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
        <Chip tone="plain">{data.progress.settled} of {data.progress.total} settled</Chip>
        <button type="button" onClick={() => navigate(`/teacher/events/${id}/record`)} style={{ ...S.link, border: 'none', background: 'none' }}>
          See the event record
        </button>
      </div>
    </>,
  );
}
